import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { CheckCircle2, MessageSquare, Clock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePreserveSearchParams } from "@/hooks/usePreserveSearchParams";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { de, enUS } from "date-fns/locale";

export const PendingTasks: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { currentTenant } = useTenant();
  const { createPath } = usePreserveSearchParams();
  const queryClient = useQueryClient();

  const { data: pendingNotes, isLoading } = useQuery({
    queryKey: ["pending-notes", currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];

      const { data, error } = await supabase
        .from("notes")
        .select(`
          id,
          content,
          created_at,
          conversation_id,
          conversations (
            customer_id,
            customers (
              first_name,
              last_name
            ),
            messages (
              channel
            )
          )
        `)
        .eq("tenant_id", currentTenant.id)
        .eq("action_status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!currentTenant?.id,
  });

  const completeTaskMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const { error } = await supabase
        .from("notes")
        .update({ action_status: "completed" })
        .eq("id", noteId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t("taskCompleted"));
      queryClient.invalidateQueries({ queryKey: ["pending-notes", currentTenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["pending-tasks-count", currentTenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["tasks", currentTenant?.id] });
    },
    onError: () => {
      toast.error(t("failedToCompleteTask"));
    },
  });

  const handleCompleteTask = (noteId: string) => {
    completeTaskMutation.mutate(noteId);
  };

  const formatTimeAgo = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: i18n.language === "de" ? de : enUS,
    });
  };

  // Section heading on the page (matches TodayVisits / Alea section pattern).
  const sectionHeader = (
    <div className="flex items-center gap-2 px-1">
      <CheckCircle2 className="h-5 w-5 text-primary" />
      <h2 className="text-lg font-medium tracking-tight text-foreground-strong">
        {t("tasks")}
      </h2>
    </div>
  );

  if (isLoading) {
    return (
      <section className="space-y-3">
        {sectionHeader}
        <Card className="overflow-hidden">
          <CardContent>
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
              <div className="text-muted-foreground">{t("loading")}</div>
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="space-y-3">
      {sectionHeader}
      <Card className="overflow-hidden">
      <CardContent className="p-0">
        {pendingNotes && pendingNotes.length > 0 ? (
          <div className="divide-y divide-border">
            {pendingNotes.map((note) => {
              const customerName = note.conversations?.customers
                ? `${note.conversations.customers.first_name || ""} ${note.conversations.customers.last_name || ""}`.trim()
                : null;

              return (
                <div
                  key={note.id}
                  className="p-4 transition-colors hover:bg-muted/50"
                >
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1 space-y-2">
                      <p className="text-sm font-medium text-foreground whitespace-pre-wrap leading-relaxed">
                        {note.content}
                      </p>

                      <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-muted">
                          <Clock className="h-3 w-3 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            {formatTimeAgo(note.created_at)}
                          </span>
                        </div>

                        {customerName && (
                          <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-primary/10">
                            <User className="h-3 w-3 text-primary" />
                            <span className="text-sm text-primary">
                              {t("taskFor")}: {customerName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0 items-stretch sm:items-center">
                      {note.conversations && note.conversations.customer_id && note.conversation_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (note.conversations?.customer_id && note.conversation_id) {
                              // Get the channel from the first message in the conversation
                              const channel = note.conversations?.messages?.[0]?.channel;
                              const customerId = note.conversations.customer_id;

                              // Voice conversations go to transcript route, others go to customer conversation
                              const path = channel === 'voice'
                                ? `/conversations/customer/${customerId}/transcript/${note.conversation_id}`
                                : `/conversations/customer/${customerId}`;

                              navigate(createPath(path));
                            }
                          }}
                          className="flex items-center gap-1"
                        >
                          <MessageSquare className="h-3 w-3" />
                          {t("viewConversation")}
                        </Button>
                      )}

                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handleCompleteTask(note.id)}
                        disabled={completeTaskMutation.isPending}
                        className="flex items-center gap-1 bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle2 className="h-3 w-3" />
                        {t("markAsDone")}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-6">
            <CheckCircle2 className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground text-center">{t("noPendingTasks")}</p>
          </div>
        )}
      </CardContent>
      </Card>
    </section>
  );
};
